import ReactEcharts from 'echarts-for-react';
import { useEffect, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { ChartControlsProps } from '../../redux/ChartPoperties/ChartControlsInterface';
import { RootState } from '../../redux';

import {
  formatChartLabelValueForSelectedMeasure,
  formatChartXAxisValue,
  formatChartYAxisValue,
} from '../ChartOptions/Format/NumberFormatter';
import {
  ChartsMapStateToProps,
  ChartsReduxStateProps,
  FormatterValueProps,
} from './ChartsCommonInterfaces';
import { palette } from '../..';
import {
  checkUidPresenceInCheckedMeasureForTooltip,
  limitChartData,
  shouldChartDisplayWithSecurity,
} from '../CommonFunctions/CommonFunctions';
import EmptyQuery from './EmptyQuery';
import { useDispatch } from 'react-redux';
import { updateAxisOptions } from '../../redux/ChartPoperties/ChartControlsActions';
import { updateHoveredItems } from '../../redux/TabTile/TabActions';
import { tooltipStyle } from '../Common/EChartTooltip';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { messages } from '../CommonFunctions/aliases';
import { toast } from 'react-toastify';
import {
  AXIS_THRESHOLDS,
  LABEL_THRESHOLDS,
  LEGEND_THRESHOLDS,
  MARGIN_THRESHOLDS,
} from '../ChartOptions/ChartThresholds';
import { compareWithLogicalOrder } from '../ChartOptions/Sort/dataOrdeingUtils';

const Horizontalstacked = ({
  //props
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  colorScheme,
  fontFamily,
  softUI,

  //state
  chartControls,
  chartProperties,
}: ChartsReduxStateProps) => {
  // TODO: propblem in applying filters

  const {
    selectedMeasureName,
    tabState,
    selectedTab,
    showDash,
    tooltipFormats,
    chartAxes,
    isTooltipEnabled,
    chartType,
    sortConfigurations,
  } = useSelector((state: RootState) => ({
    selectedMeasureName:
      state.chartControls?.properties[propKey]?.formatOptions?.dateFormats?.selectedMeasureName,
    tooltipFormats: state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats,
    chartAxes: state.chartProperties?.properties[propKey]?.chartAxes,
    isTooltipEnabled:
      state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable,
    tabState: state.tabState,
    selectedTab: state.tabTileProps.selectedTabId,
    showDash: state.tabTileProps.showDash,
    chartType: state.chartProperties?.properties[propKey]?.chartType,
    sortConfigurations: state.chartControls?.properties[propKey]?.sortConfigurations || [],
  }));

  const chartControl: ChartControlsProps = chartControls.properties[propKey];
  const currentControls = chartControl.horizontalStackedBarChartControls || {};
  const { manualColor, nodeValuesColors, allSeriesColor } = currentControls;
  let chartData: any[] = chartControl.chartData ? chartControl.chartData : [];
  const chartRef = useRef<any>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const dispatch = useDispatch();

  const [seriesData, setSeriesData] = useState<any>([]);

  const [highlightedIndices, setHighlightedIndices] = useState<number[] | null>(null);

  const mouseOverInteractivity =
    tabState.tabs[selectedTab]?.interactivity?.mouseOverInteractivity || false;

  const limitedChartData = limitChartData(chartData, 100, chartAxes[3]?.fields[0]?.displayname);

  const processedChartData = limitedChartData.map((item) => {
    return Object.fromEntries(
      Object.entries(item).map(([key, value]) => [
        key,
        value === null
          ? '(Blank)'
          : typeof value === 'boolean'
            ? value
              ? 'True'
              : 'False'
            : value,
      ]),
    );
  });

  const numDimensions = chartProperties.properties[propKey].chartAxes[1].fields.length;

  useEffect(() => {
    if (numDimensions === 2) {
      // If dimension count is 2, rotate the y-axis labels
      dispatch(updateAxisOptions(propKey, 'yAxis', 'tickRotationLeft', 90));
      dispatch(updateAxisOptions(propKey, 'yAxis', 'tickRotationRight', 90));
    }
  }, [numDimensions]);

  useEffect(() => {
    if (chartData.length > 100 && !showDash) {
      toast.warning(`${messages.chart.excessiveData} 100 records`, {
        position: 'top-center',
        autoClose: 10000,
        toastId: 'chart-data-limit-warning',
        style: {
          top: '10px',
        },
      });
    }
  }, [chartData]);

  const xAxisName = (processedChartData[0] && Object.keys(processedChartData[0])[0]) || '';

  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (!chartControl.colorScheme || chartArea === 'dashboard') {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });
  const selectedTheme = chartThemes.length > 0 ? chartThemes[0] : allThemes[0];
  const themeColors = selectedTheme?.colors || [];

  // --- Helper to get color ---
  const getSeriesColor = (name: string, index: number) => {
    if (manualColor) {
      if (nodeValuesColors && nodeValuesColors[name]) {
        return nodeValuesColors[name];
      }
      if (allSeriesColor) {
        return allSeriesColor;
      }
    }
    return themeColors[index % themeColors.length];
  };
  const dimensionKeys = chartData[0] && Object.keys(chartData[0]).slice(0, numDimensions).reverse();

  const yAxes = dimensionKeys?.map((dim: any, index: number) => ({
    type: 'category',
    offset: index * 18,

    splitLine: {
      show: chartControl.axisOptions?.ySplitLine,
      lineStyle: {
        color: chartControl.axisOptions.yAxis[1].splitLineColor || '#d3d3d3',
        width: chartControl.axisOptions.yAxis[1].splitLineWidth ?? 1,
        type: chartControl.axisOptions.yAxis[1].splitLineType || 'solid',
      },
    },
    inverse: !chartControl.axisOptions.inverse,

    position: chartControl.axisOptions.yAxis[1].position,

    axisLine: {
      show:
        chartControl.axisOptions.yAxis[1].position !== 'hide' &&
        index === 0 &&
        Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h, // Only show for first dimension
      onZero:
        chartControl.axisOptions.yAxis[1].position === 'bottom'
          ? false
          : chartControl.axisOptions.xAxis[1].onZero,
    },

    show: true,

    name:
      index === numDimensions - 1 && Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
        ? chartControl.axisOptions.yAxis[1].name
        : '', // To display name only for last dimension
    nameLocation: chartControl.axisOptions.yAxis[1].nameLocation,
    nameGap: chartControl.axisOptions.yAxis[1].nameGap,
    nameTextStyle: {
      fontSize: chartControl.axisOptions.yAxis[1].nameSize,
      color: chartControl.axisOptions.yAxis[1].nameColor,
    },

    axisTick: {
      show:
        chartControl.axisOptions.yAxis[1].position !== 'hide' &&
        index === 0 &&
        Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h, // Only show for first dimension
      alignWithLabel: true,
      length:
        chartControl.axisOptions.yAxis[1].position === 'left'
          ? chartControl.axisOptions.yAxis[1].tickSizeLeft
          : chartControl.axisOptions.yAxis[1].tickSizeRight,
    },
    axisLabel: {
      show:
        chartControl.axisOptions.yAxis[1].position !== 'hide' &&
        Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
      fontSize: chartControl.axisOptions.yAxis[1].fontSize,
      rotate:
        chartControl.axisOptions.yAxis[1].position === 'left'
          ? chartControl.axisOptions.yAxis[1].tickRotationLeft
          : chartControl.axisOptions.yAxis[1].tickRotationRight,
      margin:
        chartControl.axisOptions.yAxis[1].position === 'left'
          ? chartControl.axisOptions.yAxis[1].tickPaddingLeft
          : chartControl.axisOptions.yAxis[1].tickPaddingRight,
      color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
      fontFamily: fontFamily,
      formatter: (value: any) => {
        const fieldInAxes = chartAxes
          ?.flatMap((axis: any) => axis?.fields || [])
          ?.find((f: any) => f.displayname === xAxisName);

        const measureUid = fieldInAxes?.uId;

        const labelFormat = chartControl?.formatOptions?.labelFormats?.measureFormats?.[
          measureUid
        ] as any;

        if (labelFormat?.finalDateFormat) {
          return formatChartXAxisValue(chartControl, value, labelFormat);
        }
        return value;
      },
    },

    data: processedChartData.map((item) => item[dim]),
  }));

  const getTopMarginForLegend = () => {
    var top = '';
    if (chartControl.legendOptions?.position?.top === 'top') {
      top = 'top';
      // } else if (chartControl.legendOptions?.position?.top === "bottom") {
      //   top = "90%";
    } else {
      top = '50%';
    }
    return top;
  };

  const [hoveredColumns, setHoveredColumns] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!mouseOverInteractivity) return;
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || chartInstance.isDisposed?.()) return;

      let matchingIndices = processedChartData
        .map((item, index) => {
          const isMatch = tabState.tabs[selectedTab].hoveredItems?.some(
            (hovered: Record<string, any>) =>
              String(hovered[dimensionKeys[0]]) === String(item[dimensionKeys[0]]),
          );
          return isMatch ? index : -1;
        })
        .filter((index) => index !== -1);

      if (dimensionKeys.length > 1) {
        let matchingIndicesForDim2 = processedChartData
          .map((item, index) => {
            const isMatch = tabState.tabs[selectedTab].hoveredItems?.some(
              (hovered: Record<string, any>) =>
                String(hovered[dimensionKeys[1]]) === String(item[dimensionKeys[1]]),
            );
            return isMatch ? index : -1;
          })
          .filter((index) => index !== -1);
        if (matchingIndicesForDim2.length > 0 && matchingIndices.length > 0) {
          matchingIndices = matchingIndices.filter((ind) => matchingIndicesForDim2.includes(ind));
        } else if (matchingIndicesForDim2.length > 0) {
          matchingIndices = matchingIndicesForDim2;
        }
      }

      setHighlightedIndices(matchingIndices);
    }, 30); // slight delay helps when charts update

    return () => clearTimeout(timeout);
  }, [tabState.tabs[selectedTab].hoveredItems, chartData]);

  const hoverdTimeout = useRef<NodeJS.Timeout | null>(null);
  const hoveredRef = useRef<Record<string, any> | null>(null);

  const handleChartReady = (chartInstance: any) => {
    if (!mouseOverInteractivity) return;

    chartInstanceRef.current = chartInstance;

    chartInstance.on('mouseover', (params: any) => {
      if (
        params?.seriesName &&
        params?.name &&
        hoveredRef.current?.[dimensionKeys[0]] !== params.name
      ) {
        if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);

        hoverdTimeout.current = setTimeout(() => {
          const newEntry = { [dimensionKeys[0]]: params.name };
          const newEntry_1 = {
            [dimensionKeys[0]]: processedChartData[params.dataIndex][dimensionKeys[0]],
          };
          const newEntry_2 = {
            [dimensionKeys[1]]: processedChartData[params.dataIndex][dimensionKeys[1]],
          };
          hoveredRef.current = newEntry;

          setHoveredColumns((prev) => {
            if (dimensionKeys.length === 1) {
              const isDuplicate = prev.some((item) => item[dimensionKeys[0]] === params.name);
              if (!isDuplicate) {
                return [...prev, newEntry];
              }
            }
            if (dimensionKeys.length === 2) {
              return [...prev, newEntry_1, newEntry_2];
            }
            return prev;
          });
        }, 200);
      }
    });

    chartInstance.on('mouseout', (params: any) => {
      if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);
      if (params?.name) {
        hoveredRef.current = null;
        setHoveredColumns([]);
      }
    });
  };

  useEffect(() => {
    dispatch(updateHoveredItems(selectedTab, hoveredColumns, propKey));
  }, [hoveredColumns]);

  const chartInstance = chartRef.current?.getEchartsInstance?.();
  const currentOption = chartInstance?.getOption();
  const [currentLegendSelected, setCurrentLegendSelected] = useState<any>(
    currentOption?.legend?.[0]?.selected ?? {},
  );
  const hasSplitBy = chartProperties.properties[propKey].chartAxes[2]?.fields?.length > 0;

  const buildSeries = () => {
    if (!processedChartData || !processedChartData.length) return [];

    const keys = Object.keys(processedChartData[0]);

    const axisMinMax = chartControl.axisOptions.axisMinMax;
    const getClampedValue = (val: any) => {
      if (val === null || val === undefined || isNaN(val)) return val;
      let finalVal = val;

      if (axisMinMax.enableMax && axisMinMax.maxValue !== null && val > axisMinMax.maxValue) {
        finalVal = axisMinMax.maxValue;
      }
      if (axisMinMax.enableMin && axisMinMax.minValue !== null && val < axisMinMax.minValue) {
        finalVal = axisMinMax.minValue;
      }
      return finalVal;
    };

    const computeItemOpacity = (idx: number) => {
      if (tabState.tabs[selectedTab].hoveredPropkey !== propKey) {
        if (!highlightedIndices || !highlightedIndices.length) return 1;
        return highlightedIndices.includes(idx) ? 1 : 0.5;
      }
      return 1;
    };

    let seriesArr: any[] = [];
    const dimensionFields = chartProperties.properties[propKey].chartAxes[1]?.fields || [];
    const measureFields = chartProperties.properties[propKey].chartAxes[3]?.fields || [];

    if (hasSplitBy) {
      const splitField = chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname;
      const measureField = chartProperties.properties[propKey].chartAxes[3]?.fields[0]?.displayname;

      const dimensionFields = keys.slice(0, numDimensions);
      const makeComboKey = (row: any) => dimensionFields.map((f) => String(row[f])).join('||@||');

      const uniqueCombos: string[] = [];
      const comboFirstIndex: Record<string, number> = {};
      const comboSampleRow: Record<string, any> = {};

      processedChartData.forEach((row, idx) => {
        const k = makeComboKey(row);
        if (!uniqueCombos.includes(k)) {
          uniqueCombos.push(k);
          comboFirstIndex[k] = idx;
          comboSampleRow[k] = row;
        }
      });

      const splitByFieldObj = chartProperties.properties[propKey].chartAxes[2]?.fields[0];
      const splitFieldConfig = sortConfigurations.find(
        (config: any) => config.fieldId === splitByFieldObj?.uId,
      );

      const groups = Array.from(new Set(processedChartData.map((d) => String(d[splitField])))).sort(
        (a, b) => {
          // Fallback to logical ascending order when no sort config exists
          if (!splitFieldConfig || splitFieldConfig.sortOrder === '') {
            return compareWithLogicalOrder(a, b, true);
          } else if (splitFieldConfig.sortOrder === 'Manual Sort') {
            if (splitFieldConfig.manualSortOrderObject) {
              return (
                (splitFieldConfig.manualSortOrderObject[a] ?? 999999) -
                (splitFieldConfig.manualSortOrderObject[b] ?? 999999)
              );
            } else {
              // Sorting in ascending order if no manual sort order is available
              return compareWithLogicalOrder(a, b, true);
            }
          } else {
            const isAscending = splitFieldConfig.sortOrder === 'Ascending';
            return compareWithLogicalOrder(a, b, isAscending);
          }
        },
      );

      const valMap = new Map<string, number | null>();
      processedChartData.forEach((row) => {
        const k = makeComboKey(row);
        const grp = String(row[splitField]);
        const val = Number(row[measureField]) || 0;
        valMap.set(`${k}__${grp}`, val);
      });

      seriesArr = groups.map((group, i) => {
        const seriesColor = getSeriesColor(String(group), i);

        const data = uniqueCombos.map((k) => {
          const idx = comboFirstIndex[k];
          const rawVal = valMap.get(`${k}__${group}`);
          const plottedVal = rawVal != null ? getClampedValue(rawVal) : null;

          return {
            value: plottedVal,
            originalValue: rawVal,
            itemStyle:
              tabState.tabs[selectedTab].hoveredPropkey !== propKey
                ? { opacity: computeItemOpacity(idx) }
                : {},
          };
        });

        return {
          type: 'bar',
          name: String(group),
          data,
          stack: 'stack',
          emphasis: {
            focus: tabState.tabs[selectedTab].hoveredPropkey !== propKey ? 'none' : 'self',
          },
          blur: { itemStyle: { opacity: 0.5 } },
          itemStyle: softUI
            ? {
                color: seriesColor,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
                shadowBlur: 10,
                shadowOffsetX: 3,
                shadowOffsetY: 3,
              }
            : { color: seriesColor },
          label: {
            show:
              Math.floor(graphDimension.height) > LABEL_THRESHOLDS.common.h
                ? chartControl.labelOptions.showLabel
                : false,
            fontSize: chartControl.labelOptions.fontSize,
            fontFamily: fontFamily,
            color: chartControl.labelOptions.labelColor
              ? chartControl.labelOptions.labelColor
              : selectedTheme?.dark
                ? '#ffffff'
                : '#757474',
            backgroundColor: chartControl.labelOptions.labelBackground
              ? chartControl.labelOptions.labelBackgroundColor
              : 'transparent',
            padding: chartControl.labelOptions.labelBackground ? [4, 8] : 0,
            borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
            formatter: (value: FormatterValueProps) => {
              const val = value.data?.originalValue ?? value.value;
              return formatChartLabelValueForSelectedMeasure(
                chartControls.properties[propKey],
                chartProperties.properties[propKey],
                val,
                measureField,
              );
            },
          },
        };
      });

      if (Array.isArray(yAxes) && yAxes.length > 0) {
        yAxes[0].data = uniqueCombos.map((k) => String(comboSampleRow[k][dimensionFields[0]]));
        if (yAxes.length > 1 && dimensionFields[1]) {
          yAxes[1].data = uniqueCombos.map((k) => String(comboSampleRow[k][dimensionFields[1]]));
        }
      }
    } else {
      const measureKeys = keys.slice(numDimensions);

      const isColorByDimension = measureFields.length === 1 && dimensionFields.length > 0;
      let uniqueDimValues: string[] = [];
      let lastDimFieldName = '';

      if (isColorByDimension) {
        const lastDimObj = dimensionFields[dimensionFields.length - 1];
        lastDimFieldName = lastDimObj.displayname || lastDimObj.fieldname;
        uniqueDimValues = Array.from(
          new Set(processedChartData.map((d) => String(d[lastDimFieldName]))),
        );
      }

      seriesArr = measureKeys.map((measure, i) => {
        const baseSeriesColor = getSeriesColor(measure, i);

        const data = processedChartData.map((item, idx) => {
          let itemColor = baseSeriesColor;

          if (isColorByDimension) {
            const dimValue = String(item[lastDimFieldName]);

            if (manualColor) {
              if (nodeValuesColors && nodeValuesColors[dimValue]) {
                itemColor = nodeValuesColors[dimValue];
              } else if (allSeriesColor) {
                itemColor = allSeriesColor;
              } else {
                // In manual mode without explicit colors, use first theme color
                itemColor = themeColors[0];
              }
            } else {
              // In auto mode with 1 measure + dimensions, use first theme color for all bars
              itemColor = themeColors[0];
            }
          }

          const rawVal = item[measure];
          const plottedVal = getClampedValue(rawVal);

          return {
            value: plottedVal,
            originalValue: rawVal,
            itemStyle: {
              color: itemColor,
              ...(tabState.tabs[selectedTab].hoveredPropkey !== propKey
                ? { opacity: computeItemOpacity(idx) }
                : {}),
              ...(softUI
                ? {
                    shadowColor: 'rgba(0, 0, 0, 0.5)',
                    shadowBlur: 10,
                    shadowOffsetX: 3,
                    shadowOffsetY: 3,
                  }
                : {}),
            },
          };
        });

        return {
          type: 'bar',
          name: measure,
          data,
          stack: 'stack',
          emphasis: {
            focus: tabState.tabs[selectedTab].hoveredPropkey !== propKey ? 'none' : 'self',
          },
          blur: { itemStyle: { opacity: 0.5 } },

          itemStyle: !isColorByDimension
            ? softUI
              ? {
                  color: baseSeriesColor,
                  shadowColor: 'rgba(0, 0, 0, 0.5)',
                  shadowBlur: 10,
                  shadowOffsetX: 3,
                  shadowOffsetY: 3,
                }
              : { color: baseSeriesColor }
            : {},

          label: {
            show:
              Math.floor(graphDimension.height) > LABEL_THRESHOLDS.common.h
                ? chartControl.labelOptions.showLabel
                : false,
            fontSize: chartControl.labelOptions.fontSize,
            fontFamily: fontFamily,
            color: chartControl.labelOptions.labelColor
              ? chartControl.labelOptions.labelColor
              : selectedTheme?.dark
                ? '#ffffff'
                : '#757474',
            backgroundColor: chartControl.labelOptions.labelBackground
              ? chartControl.labelOptions.labelBackgroundColor
              : 'transparent',
            padding: chartControl.labelOptions.labelBackground ? [2, 4] : 0,
            borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
            formatter: (value: FormatterValueProps) => {
              const val = value.data?.originalValue ?? value.value;
              return formatChartLabelValueForSelectedMeasure(
                chartControls.properties[propKey],
                chartProperties.properties[propKey],
                val,
                measure,
              );
            },
          },
        };
      });
    }

    return seriesArr;
  };

  const onLegendSelectChanged = (params: any) => {
    setCurrentLegendSelected(params.selected);
  };

  const RenderChart = () => {
    return (
      <>
        <ReactEcharts
          ref={chartRef}
          onChartReady={handleChartReady}
          onEvents={{
            legendselectchanged: onLegendSelectChanged,
          }}
          // theme={chartControl.colorScheme}
          style={{
            // padding: "1rem",
            width: graphDimension.width,
            height: graphDimension.height,
            overflow: 'hidden',
            margin: 'auto',
            border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
          }}
          option={{
            color: selectedTheme?.colors || [],
            backgroundColor: selectedTheme?.background || [],
            animation: false,
            legend: {
              // textStyle :{color : getContrastColor(chartThemes[0].background)},
              type: 'scroll',
              show:
                Math.floor(graphDimension.height) > LEGEND_THRESHOLDS.horizontalBar.h
                  ? chartControl.legendOptions?.showLegend
                  : false,
              itemHeight: chartControl.legendOptions?.symbolHeight,
              itemWidth: chartControl.legendOptions?.symbolWidth,
              itemGap: chartControl.legendOptions?.itemGap,

              left: chartControl.legendOptions?.position?.left,
              top:
                chartControl.legendOptions?.position?.top !== 'bottom'
                  ? getTopMarginForLegend()
                  : null,
              // top: chartControl.legendOptions?.position?.top,
              bottom: chartControl.legendOptions?.position?.top === 'bottom' ? 0 : null,
              orient: chartControl.legendOptions?.orientation,
              selected: currentLegendSelected,
              textStyle: {
                color: chartThemes[0]?.dark ? '#ffffff' : palette.primary.contrastText,
                fontFamily: fontFamily,
              },
            },
            grid: {
              left:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? 5
                  : (chartControl.chartMargin.horizontalLeft ?? chartControl.chartMargin.left) +
                    5 +
                    '%',
              right:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? 5
                  : chartControl.chartMargin.right + '%',
              top:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? 5
                  : chartControl.legendOptions?.position?.top === 'top'
                    ? chartControl.chartMargin.top + 5 + '%'
                    : chartControl.chartMargin.top + '%',
              bottom:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? 5
                  : chartControl.legendOptions?.position?.top === 'bottom'
                    ? (graphDimension.height * chartControl.chartMargin.bottom) / 100 + 35
                    : chartControl.chartMargin.bottom + '%',
              itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
                shadowOffsetX: 5,
                shadowOffsetY: 5,
              },
            },

            tooltip: {
              show: isTooltipEnabled,
              useHTML: true,
              trigger: 'item',
              backgroundColor: 'white',
              borderWidth: 1,
              padding: 0,
              textStyle: {
                color: '#333',
                fontSize: 16,
              },
              appendToBody: true,
              extraCssText:
                'box-shadow: 0 2px 8px rgba(0,0,0,0.15);white-space: normal; word-break: break-word;',
              formatter: function (params: any) {
                let row: any = null;
                let hoveredFieldname: string = '';

                const dimensionAxis = chartAxes[1];
                const dimensionAxisDisplayNames =
                  dimensionAxis?.fields?.map((field: any) => field.displayname) || [];

                if (hasSplitBy) {
                  const splitField =
                    chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname;
                  const measureField =
                    chartProperties.properties[propKey].chartAxes[3]?.fields[0]?.displayname;

                  // Build the same composite key order used in buildSeries
                  const makeComboKey = (r: any) =>
                    dimensionAxisDisplayNames.map((f: string) => String(r[f])).join('||@||');

                  const uniqueComboKeys: string[] = [];
                  const uniqueComboSampleRows: any[] = [];
                  processedChartData.forEach((r: any) => {
                    const k = makeComboKey(r);
                    if (!uniqueComboKeys.includes(k)) {
                      uniqueComboKeys.push(k);
                      uniqueComboSampleRows.push(r);
                    }
                  });

                  const sample = uniqueComboSampleRows[params.dataIndex];

                  row =
                    sample &&
                    processedChartData.find(
                      (r: any) =>
                        dimensionAxisDisplayNames.every(
                          (f: string) => String(r[f]) === String(sample[f]),
                        ) && String(r[splitField]) === String(params.seriesName),
                    );

                  if (!row) row = sample;
                  if (!row) return '';

                  hoveredFieldname = measureField;

                  const tempData: Record<string, any> = {
                    ...Object.fromEntries(
                      dimensionAxisDisplayNames.map((key: any) => [key, row[key]]),
                    ),
                    [splitField]: row?.[splitField],
                    [measureField]: row?.[measureField],
                  };

                  const allFieldName = [...dimensionAxisDisplayNames, hoveredFieldname, splitField];

                  //---------get list of non hovered field -------//
                  const nonHoveredFieldList = [
                    ...new Set(
                      processedChartData
                        .map((data: any) =>
                          Object.keys(data).filter((key: string) => !allFieldName.includes(key)),
                        )
                        .flat(),
                    ),
                  ];

                  //----------remove unchecked measure or any other field value of type date inside tooltip----------//
                  const _data: { [key: string]: string | number } = {};
                  Object.keys(tempData).forEach((key: string) => {
                    const resp = checkUidPresenceInCheckedMeasureForTooltip(
                      chartAxes,
                      key,
                      tooltipFormats?.checkedMeasure,
                    );
                    if (resp) {
                      _data[key] = tempData[key];
                    }
                  });
                  let mergedData = { ..._data };

                  const extraResponses = Array.isArray(tooltipFormats?.extraMeasureFieldsResponses)
                    ? tooltipFormats.extraMeasureFieldsResponses
                    : [];
                  //-- send extra responses in _data so that it maps and shows all key value pairs including extra measure (if any)--//
                  if (extraResponses.length > 0) {
                    const lookupKeys = [...dimensionAxisDisplayNames, splitField];
                    const tooltipData = extraResponses.find((obj: any) =>
                      lookupKeys.every((dimensionKey: string) => {
                        const dimensionValue = tempData[dimensionKey as string];
                        return (
                          dimensionValue !== undefined &&
                          String(obj[dimensionKey as string]) === String(dimensionValue as string)
                        );
                      }),
                    );

                    let filteredFields = tooltipData ? { ...tooltipData } : {};
                    if (filteredFields) {
                      lookupKeys.forEach((dimensionKey: string) => {
                        delete filteredFields[dimensionKey];
                      });
                    }

                    if (tooltipData && Object.keys(filteredFields).length > 0) {
                      const isHoveredFieldChecked = checkUidPresenceInCheckedMeasureForTooltip(
                        chartAxes,
                        hoveredFieldname,
                        tooltipFormats?.checkedMeasure,
                      );

                      if (!isHoveredFieldChecked) {
                        delete filteredFields[hoveredFieldname];
                      }

                      Object.keys(filteredFields).forEach((key) => {
                        if (
                          nonHoveredFieldList.includes(key) ||
                          !checkUidPresenceInCheckedMeasureForTooltip(
                            chartAxes,
                            key,
                            tooltipFormats?.checkedMeasure,
                          )
                        ) {
                          delete filteredFields[key];
                        }
                      });

                      mergedData = { ...mergedData, ...filteredFields };
                    }
                  }

                  const extendedDimensionNames = [...dimensionAxisDisplayNames, splitField];

                  return tooltipStyle(
                    params.color,
                    mergedData,
                    tooltipFormats,
                    chartAxes,
                    extendedDimensionNames,
                    undefined,
                    undefined,
                    fontFamily,
                  );
                } else {
                  row = processedChartData[params.dataIndex];
                  if (!row) return '';

                  hoveredFieldname = params.seriesName;

                  const allFieldName = [...dimensionAxisDisplayNames, hoveredFieldname];
                  const tempData: Record<string, any> = {};
                  allFieldName.forEach((key: string) => (tempData[key] = row[key]));

                  const nonHoveredFieldList = [
                    ...new Set(
                      processedChartData
                        .map((data: any) =>
                          Object.keys(data).filter((key: string) => !allFieldName.includes(key)),
                        )
                        .flat(),
                    ),
                  ];

                  const _data: { [key: string]: string | number } = {};
                  Object.keys(tempData).forEach((key: string) => {
                    const resp = checkUidPresenceInCheckedMeasureForTooltip(
                      chartAxes,
                      key,
                      tooltipFormats?.checkedMeasure,
                    );
                    if (resp) {
                      _data[key] = tempData[key];
                    }
                  });
                  let mergedData = { ..._data };

                  const extraResponses = tooltipFormats?.extraMeasureFieldsResponses || [];
                  if (tooltipFormats && extraResponses.length > 0) {
                    const tooltipData = extraResponses.find((obj: any) => {
                      return dimensionAxisDisplayNames.every((dimensionKey: string) => {
                        const dimensionValue = tempData[dimensionKey as string];
                        return (
                          String(obj[dimensionKey as string]) === String(dimensionValue as string)
                        );
                      });
                    });

                    let filteredFields = tooltipData ? { ...tooltipData } : {};
                    if (filteredFields) {
                      dimensionAxisDisplayNames.forEach((dimensionKey: string) => {
                        delete filteredFields[dimensionKey];
                      });
                    }

                    if (tooltipData && Object.keys(filteredFields).length > 0) {
                      const isPresent = checkUidPresenceInCheckedMeasureForTooltip(
                        chartAxes,
                        hoveredFieldname,
                        tooltipFormats?.checkedMeasure,
                      );

                      if (!isPresent) {
                        delete filteredFields[hoveredFieldname];
                      }

                      for (let key in filteredFields) {
                        if (nonHoveredFieldList.includes(key)) {
                          delete filteredFields[key];
                        }
                      }
                      mergedData = { ...mergedData, ...filteredFields };
                    }
                  }

                  return tooltipStyle(
                    params.color,
                    mergedData,
                    tooltipFormats,
                    chartAxes,
                    dimensionAxisDisplayNames,
                    undefined,
                    undefined,
                    fontFamily,
                  );
                }
              },
            },

            dataset: {
              dimensions: Object.keys(processedChartData[0]),
              source: processedChartData,
            },

            xAxis: {
              splitLine: {
                show: chartControl.axisOptions?.xSplitLine,
                lineStyle: {
                  color: chartControl.axisOptions.xAxis[1].splitLineColor || '#d3d3d3',
                  width: chartControl.axisOptions.xAxis[1].splitLineWidth ?? 1,
                  type: chartControl.axisOptions.xAxis[1].splitLineType || 'solid',
                },
              },
              min: null,

              // min: chartControl.axisOptions.axisMinMax.enableMin
              //   ? chartControl.axisOptions.axisMinMax.minValue
              //   : null,
              max: chartControl.axisOptions.axisMinMax.enableMax
                ? chartControl.axisOptions.axisMinMax.maxValue
                : null,

              position: chartControl.axisOptions.xAxis[1].position,
              show: true,

              name:
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
                  ? chartControl.axisOptions.xAxis[1].name
                  : '',
              nameLocation: chartControl.axisOptions.xAxis[1].nameLocation,
              nameGap: chartControl.axisOptions.xAxis[1].nameGap,
              nameTextStyle: {
                fontSize: chartControl.axisOptions.xAxis[1].nameSize,
                color: chartControl.axisOptions.xAxis[1].nameColor,
              },

              axisLine: {
                show:
                  chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                onZero: chartControl.axisOptions.yAxis[1].onZero,
              },

              axisTick: {
                show:
                  chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                alignWithLabel: true,
                length:
                  chartControl.axisOptions.xAxis[1].position === 'top'
                    ? chartControl.axisOptions.xAxis[1].tickSizeTop
                    : chartControl.axisOptions.xAxis[1].tickSizeBottom,
              },
              axisLabel: {
                show:
                  chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                fontSize: chartControl.axisOptions.xAxis[1].fontSize,
                rotate:
                  chartControl.axisOptions.xAxis[1].position === 'top'
                    ? chartControl.axisOptions.xAxis[1].tickRotationTop
                    : chartControl.axisOptions.xAxis[1].tickRotationBottom,
                margin:
                  chartControl.axisOptions.xAxis[1].position === 'top'
                    ? chartControl.axisOptions.xAxis[1].tickPaddingTop
                    : chartControl.axisOptions.xAxis[1].tickPaddingBottom,
                color: chartThemes[0]?.dark ? '#ffffff' : palette.primary.contrastText,
                fontFamily: fontFamily,
                formatter: (value: number) => {
                  var formattedValue: string = formatChartYAxisValue(chartControl, value);
                  return formattedValue;
                },
              },
            },

            yAxis: yAxes,
            series: buildSeries(),
          }}
        />
      </>
    );
  };

  const emptyQueryCondition =
    shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) === false ||
    chartData.length < 1 ||
    numDimensions < 1;

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) ? true : false
      }
    />
  ) : (
    <RenderChart />
  );
};

const mapStateToProps = (state: ChartsMapStateToProps, ownProps: any) => {
  return {
    chartProperties: state.chartProperties,
    chartControls: state.chartControls,
  };
};
export default connect(mapStateToProps, null)(Horizontalstacked);
